import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { vendorReviewService } from '@/services/VendorReviewService'
import { reviewApprovalService } from '@/services/ReviewApprovalService'
import { ownReviewService } from '@/services/OwnReviewService'
import { reviewKeys } from '@/hooks/queryKeys'
import type { EventReviewsDto, EventReviewTargetDto, OwnVendorReviewDto, OwnVendorReviewListDto, ReviewsToApproveDto } from '@lib/dto/vendor-review.dto'
import type {
  DecideVendorReviewInput,
  ReportVendorReviewInput,
  SettleVendorReviewReportInput,
  WriteVendorReviewInput,
  WriteVendorReviewReplyInput,
} from '@lib/input/vendor-review.input'

/** The event's companies to review, and the reader's review of each. */
export function useEventReviews(eventId: string): UseQueryResult<EventReviewsDto> {
  return useQuery({ queryKey: reviewKeys.event(eventId), queryFn: () => vendorReviewService.forEvent(eventId) })
}

/**
 * Write a review of one company on the event, or change one not yet published.
 * The list is read again either way: a refusal means the review was published,
 * taken down or sent again meanwhile, and the list should say so.
 */
export function useWriteReview(eventId: string): UseMutationResult<EventReviewTargetDto, Error, { vendorId: string; input: WriteVendorReviewInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ vendorId, input }) => vendorReviewService.write(eventId, vendorId, input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: reviewKeys.event(eventId) }),
  })
}

/** What waits for FLOH Venues' admins to approve. */
export function useReviewsToApprove(): UseQueryResult<ReviewsToApproveDto> {
  return useQuery({ queryKey: reviewKeys.toApprove(), queryFn: () => reviewApprovalService.waiting() })
}

/**
 * Approve or decline a review. The list is read again either way: a refusal
 * means it changed or was decided meanwhile, and the list should show it as it is.
 */
export function useDecideReview(): UseMutationResult<void, Error, { reviewId: string; input: DecideVendorReviewInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reviewId, input }) => reviewApprovalService.decide(reviewId, input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: reviewKeys.toApprove() }),
  })
}

/** The signed-in vendor company's published reviews, a page at a time. */
export function useOwnReviews(page: number): UseQueryResult<OwnVendorReviewListDto> {
  return useQuery({ queryKey: reviewKeys.own(page), queryFn: () => ownReviewService.list(page), placeholderData: keepPreviousData })
}

/**
 * The company's reply to a review, or a change to one not yet published. Its
 * reviews are read again either way: a refusal means the reply changed meanwhile.
 */
export function useReplyToReview(): UseMutationResult<OwnVendorReviewDto, Error, { reviewId: string; input: WriteVendorReviewReplyInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reviewId, input }) => ownReviewService.reply(reviewId, input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: reviewKeys.ownAll() }),
  })
}

/** Report a review of the company, once. */
export function useReportReview(): UseMutationResult<OwnVendorReviewDto, Error, { reviewId: string; input: ReportVendorReviewInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reviewId, input }) => ownReviewService.report(reviewId, input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: reviewKeys.ownAll() }),
  })
}

/** Approve or decline a company's reply, as read. */
export function useDecideReply(): UseMutationResult<void, Error, { reviewId: string; input: DecideVendorReviewInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reviewId, input }) => reviewApprovalService.decideReply(reviewId, input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: reviewKeys.toApprove() }),
  })
}

/** Keep a reported review, or take it down for good. */
export function useSettleReport(): UseMutationResult<void, Error, { reviewId: string; input: SettleVendorReviewReportInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reviewId, input }) => reviewApprovalService.settleReport(reviewId, input),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: reviewKeys.toApprove() }),
  })
}
